import classNames from 'classnames';
import { useEffect, useState } from 'react';
import Typography from '@/components/atoms/typography/typography';
import Button from '@/components/molecules/button/button';
import { EditedPassenger } from '@/components/organisms/edit-pax-details/edit-pax-details';
import EditRoomOccupants from '@/components/organisms/edit-room-occupants/edit-room-occupants';
import Lightbox from '@/components/utilities/lightbox/lightbox';
import useMutationUpdatePreBookingPassengers from '@/integrations/booking/pre-booking/passengers/update-passengers/update-passengers.hooks';
import {
  DATE_OF_BIRTH_DEPENDENT_QUERIES,
  NAME_DEPENDENT_QUERIES,
  QueryId,
} from '@/integrations/content/utils';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import styles from './edit-passengers-list-modal.module.scss';

export interface EditPassengersListModalProps {
  /** Text to show for agree send email checkbox */
  agreeSendEmailText?: string;
  /** Text to show for agree send sms checkbox */
  agreeSendSMSText?: string;
  /** The list of available address countries */
  availableAddressCountries?: Content.Properties.AddressCountry[];
  /** The class name to apply */
  className?: string;
  /** Whether editing the date of birth is disabled */
  disableEditDoB?: boolean;
  /** Whether editing the names is disabled */
  disableEditNames?: boolean;
  /** Text to show as disclaimer */
  disclaimerText?: string;
  /** Whether email and phone are mandatory */
  emailAndPhoneMandatory?: boolean;
  /** The text to show as a headline */
  headline?: Content.Components['SimplePassengerModuleDto']['editModalHeadline'];
  /** Whether to show the address input */
  hideAddress?: boolean;
  /** Whether to show the age input */
  hideAge?: boolean;
  /** Indicates whether the modal is open. */
  isOpen?: boolean;
  /** The callback to handle the close event. */
  onClose: () => void;
  /** The parameters for the booking */
  parameters: Content.Components['IBookingHeadlessParameters'] & {
    id: string;
    pageId: string;
  };
  /** The room information of the booking */
  rooms: Content.Components['SimplePassengerModuleDto']['rooms'];
  /** Indicates whether to highlight the errors */
  shouldHighlightErrors?: boolean;
  /** Whether to show agree send email checkbox */
  showAgreeSendEmailInput?: boolean;
  /** Whether to show agree send sms checkbox */
  showAgreeSendSMSInput?: boolean;
  /** Whether to show the email input */
  showEmailInput?: boolean;
  /** Whether to show the phone input */
  showPhoneInput?: boolean;
  /** Whether to show the travel planner toggle */
  showTravelPlannerToggle?: boolean;
  /** The text to show as a subHeadline */
  subHeadline?: Content.Components['SimplePassengerModuleDto']['editModalSubHeadline'];
  /** The uniquePaxHash of the passenger as travel planner  */
  travelPlannerId?: string;
}

const EditPassengersListModal = ({
  agreeSendEmailText,
  agreeSendSMSText,
  availableAddressCountries = [],
  className,
  disableEditDoB = false,
  disableEditNames = false,
  disclaimerText,
  emailAndPhoneMandatory = false,
  headline,
  hideAddress = false,
  hideAge = false,
  parameters,
  isOpen = false,
  onClose,
  rooms,
  shouldHighlightErrors = false,
  showAgreeSendEmailInput = false,
  showAgreeSendSMSInput = false,
  showEmailInput = false,
  showPhoneInput = false,
  showTravelPlannerToggle = false,
  subHeadline,
  travelPlannerId,
}: EditPassengersListModalProps) => {
  const { t } = useI18n();
  const { brand } = usePageDetailsContext();
  const [isFormValid, setIsFormValid] = useState(false);
  const [validRooms, setValidRooms] = useState<
    { id: string; isValid: boolean }[]
  >([]);
  const [localTravelPlannerId, setLocalTravelPlannerId] =
    useState(travelPlannerId);
  const [selectedCompanionIdsByPaxHash, setSelectedCompanionIdsByPaxHash] =
    useState<Record<string, string>>({});
  const [editedPassengersCollection, setEditedPassengersCollection] = useState<
    EditedPassenger[]
  >([]);

  const hasUpdatedFields = editedPassengersCollection.length > 0;

  const {
    isLoading: isMutatingUpdatePreBookingPassengers,
    mutateAsync: mutateAsyncUpdatePreBookingPassengers,
  } = useMutationUpdatePreBookingPassengers();

  const handleCloseModal = () => {
    onClose();
    setIsFormValid(false);
    setValidRooms([]);
    setLocalTravelPlannerId(undefined);
    setSelectedCompanionIdsByPaxHash({});
    setEditedPassengersCollection([]);
  };

  const handleSubmit = async () => {
    const editedPassengersPayload: Omit<
      EditedPassenger,
      'originalFields' | 'additionalFields'
    >[] = editedPassengersCollection.map(editedPassenger => {
      return {
        uniquePaxHash: editedPassenger.uniquePaxHash,
        updatedFields: {
          ...editedPassenger.originalFields,
          ...editedPassenger.updatedFields,
        },
      };
    });

    const hasIsCustomerChange = editedPassengersPayload.some(
      ({ updatedFields }) => 'isCustomer' in updatedFields,
    );

    const hasFirstNameOrLastNameChange = editedPassengersPayload.some(
      passenger =>
        passenger.updatedFields.firstName || passenger.updatedFields.lastName,
    );

    const isDoBPresent = editedPassengersPayload.some(
      passenger => passenger.updatedFields.dateOfBirth,
    );

    const invalidationKeys = [
      ...new Set([
        QueryId.SIMPLE_PASSENGER,
        QueryId.DETAILED_PASSENGER,
        ...(isDoBPresent ? DATE_OF_BIRTH_DEPENDENT_QUERIES : []),
        ...(hasIsCustomerChange
          ? [QueryId.SIMPLE_CUSTOMER, QueryId.DETAILED_CUSTOMER]
          : []),
        ...(hasFirstNameOrLastNameChange ? NAME_DEPENDENT_QUERIES : []),
      ]),
    ];

    await mutateAsyncUpdatePreBookingPassengers({
      brand,
      invalidationKeys: invalidationKeys,
      parameters,
      passengers: editedPassengersPayload,
    });

    handleCloseModal();
  };

  const handleEditRoomOccupants = (editedPassengers: EditedPassenger[]) => {
    setEditedPassengersCollection(previous => {
      // Remove all existing entries that match the uniquePaxHash in editedPassengers
      const nonModifiedPassengers = previous.filter(
        previousPassenger =>
          !editedPassengers.some(
            editedPassenger =>
              editedPassenger.uniquePaxHash === previousPassenger.uniquePaxHash,
          ),
      );

      // Keep only editedPassengers with non-empty updatedFields
      const updatedPassengers = editedPassengers.filter(
        editedPassenger =>
          Object.keys(editedPassenger.updatedFields).length > 0 ||
          !!editedPassenger.additionalFields.hasSaveCompanionChanges,
      );

      return [...nonModifiedPassengers, ...updatedPassengers];
    });
  };

  useEffect(() => {
    const isAllRoomsValid = !validRooms.find(item => !item.isValid);

    setIsFormValid(isAllRoomsValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validRooms]);

  useEffect(() => {
    if (!isOpen) return;

    setLocalTravelPlannerId(travelPlannerId);
    setSelectedCompanionIdsByPaxHash(previousValue => {
      const roomPassengerHashes = new Set(
        rooms.flatMap(room =>
          (room.passengers ?? []).map(passenger => passenger.uniquePaxHash),
        ),
      );

      const nextValue: Record<string, string> = {};

      Object.entries(previousValue).forEach(([paxHash, companionId]) => {
        if (roomPassengerHashes.has(paxHash)) {
          nextValue[paxHash] = companionId;
        }
      });

      return nextValue;
    });
    setValidRooms(
      rooms.map(room => {
        return {
          id: room.uniqueRoomId,
          isValid: false,
        };
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <Lightbox
      className={classNames(styles.component, className)}
      isCentered
      isFullWidthOnMobile
      isOpen={isOpen}
      onClose={handleCloseModal}
      wrapperClassName={styles.lightboxWrapper}
    >
      <div className={styles.lightboxInnerWrapper}>
        <div className={styles.contentWrapper}>
          <div className={styles.topSection}>
            {(headline || subHeadline) && (
              <div className={styles.header}>
                {headline && (
                  <Typography
                    className={styles.headline}
                    tagStyle="headingMedium"
                  >
                    {headline}
                  </Typography>
                )}

                {subHeadline && (
                  <Typography
                    className={styles.subHeadline}
                    tagStyle="bodyMedium"
                  >
                    {subHeadline}
                  </Typography>
                )}
              </div>
            )}

            {rooms.map((room, roomIndex) => {
              return (
                <EditRoomOccupants
                  key={roomIndex}
                  agreeSendEmailText={agreeSendEmailText}
                  agreeSendSMSText={agreeSendSMSText}
                  availableAddressCountries={availableAddressCountries}
                  disableEditDoB={disableEditDoB}
                  disableEditNames={disableEditNames}
                  disclaimerText={disclaimerText}
                  emailAndPhoneMandatory={emailAndPhoneMandatory}
                  hideAddress={hideAddress}
                  hideAge={hideAge}
                  onChange={handleEditRoomOccupants}
                  onSelectCompanionByPaxHash={(uniquePaxHash, companionId) => {
                    setSelectedCompanionIdsByPaxHash(previousValue => {
                      const nextValue = { ...previousValue };

                      if (companionId) {
                        nextValue[uniquePaxHash] = companionId;
                      } else {
                        delete nextValue[uniquePaxHash];
                      }

                      return nextValue;
                    });
                  }}
                  onTravelPlannerToggle={uniquePaxHash =>
                    setLocalTravelPlannerId(uniquePaxHash)
                  }
                  onValidation={value => {
                    if (!shouldHighlightErrors) return;

                    setValidRooms(previousValue =>
                      previousValue.map((item, index) => ({
                        ...item,
                        isValid: index === roomIndex ? value : item.isValid,
                      })),
                    );
                  }}
                  passengers={room.passengers}
                  roomName={room.roomName}
                  roomNumber={roomIndex + 1}
                  selectedCompanionIdsByPaxHash={selectedCompanionIdsByPaxHash}
                  shouldClear={!isOpen}
                  shouldHighlightErrors={
                    shouldHighlightErrors && hasUpdatedFields && !isFormValid
                  }
                  showAgreeSendEmailInput={showAgreeSendEmailInput}
                  showAgreeSendSMSInput={showAgreeSendSMSInput}
                  showEmailInput={showEmailInput}
                  showPhoneInput={showPhoneInput}
                  showTravelPlannerToggle={showTravelPlannerToggle}
                  tooltipPosition={'right'}
                  travelPlannerId={localTravelPlannerId}
                  validationMode={'onChange'}
                />
              );
            })}
          </div>

          <div className={styles.bottomSection}>
            <Button
              className={styles.cta}
              onClick={onClose}
              text={t('items.common.words.cancel')}
              variant="secondary"
            />

            <Button
              className={styles.cta}
              disabled={
                (shouldHighlightErrors && !isFormValid) ||
                !hasUpdatedFields ||
                isMutatingUpdatePreBookingPassengers
              }
              isLoading={isMutatingUpdatePreBookingPassengers}
              onClick={() => handleSubmit()}
              text={t('items.modules.bookingFlow.save')}
              variant="primary"
            />
          </div>
        </div>
      </div>
    </Lightbox>
  );
};

EditPassengersListModal.displayName = 'EditPassengersListModal';

export default EditPassengersListModal;
